import { ApolloQueryResult, FetchResult, gql, QueryHookOptions } from '@apollo/client';
import { ClientLogger } from '../client-logger';
import {
  orgQuery,
  getAllOrgsQuery,
  orgCreateMutation,
  OrgCreateInput,
  orgCreateMutationVariables,
  orgOrgCreateMutation,
  OrgRelation,
  OrgRelationInput,
  orgsQuery,
  orgsQueryVariables,
  OrgType,
  orgUpdateMutation,
  OrgUpdateInput,
  orgUpdateMutationVariables,
  orgQueryVariables,
  generateHMACSecretMutation,
  generateHMACSecretMutationVariables,
  getAllOrgsQueryVariables,
  orgDeleteMutation,
  Role,
  orgPublicQuery,
} from './verid-api-types';
// import { orgs_orgs_edges_node } from './api-types-parts';
import { useVeridApi } from './hybrid-apollo-client';
import { useRef } from 'react';

const DEBUG = false;

// export type Org = Omit<orgs_orgs_edges_node, '__typename'>;
// export type OrgEditData = Omit<Org, 'id' | 'createdAt' | 'updatedAt'> & { jurisdictionId?: string };

export const ORG_FIELDS = gql`
  fragment Org on Org {
    id
    createdAt
    updatedAt
    name
    authConfig
    extMessageConfig
    autoOnboard
    description
    emailDomain
    externalId
    isTest
    status
    type
  }
`;

export const ORG_PUBLIC_FIELDS = gql`
  fragment OrgPublic on OrgPublic {
    id
    name
    description
    type
    status
  }
`;

export const ORG_AUTH_CONFIG_PUBLIC_FIELDS = gql`
  fragment OrgAuthConfigPublic on AuthConfigPublic {
    id
    name
    authConfigType
    redirectUrl
    clientId
    userPoolId
  }
`;

export const orgPublicQueryGQL = gql`
  ${ORG_PUBLIC_FIELDS}
  ${ORG_AUTH_CONFIG_PUBLIC_FIELDS}
  query orgPublic($id: String!, $role: Role!) {
    orgPublic(id: $id, role: $role) {
      ...OrgPublic
      authConfig {
        ...OrgAuthConfigPublic
      }
    }
  }
`;

export function useOrgService() {
  const api = useVeridApi();
  const orgsQuery = gql`
    ${ORG_FIELDS}
    query orgs($name: String, $relation: OrgRelationSearchInput, $type: OrgType, $orderBy: OrgOrder, $first: Int, $after: String) {
      orgs(query: { name: $name, type: $type, relation: $relation }, orderBy: $orderBy, first: $first, after: $after) {
        edges {
          cursor
          node {
            ...Org
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
        totalCount
      }
    }
  `;

  const apiFunctions = useRef({
    useOrgsQuery(options: QueryHookOptions<orgsQuery, orgsQueryVariables> | undefined) {
      return api.useQuery(orgsQuery, options);
    },

    useOrgsQueryFactory(options: QueryHookOptions<orgsQuery, orgsQueryVariables> | undefined) {
      return () => api.useQuery(orgsQuery, options);
    },

    useOrgsQueryProvider(options: QueryHookOptions<orgsQuery, orgsQueryVariables> | undefined) {
      if (options?.variables) {
        options.variables.type = OrgType.PROVIDER;
      } else {
        ClientLogger.error('useOrgService.useOrgsQuery', 'options.variables is undefined');
      }
      return api.useQuery(orgsQuery, options);
    },

    useOrgsQueryApp(options: QueryHookOptions<orgsQuery, orgsQueryVariables> | undefined) {
      if (options?.variables) {
        options.variables.type = OrgType.APP;
      } else {
        ClientLogger.error('useOrgService.useOrgsQuery', 'options.variables is undefined');
      }
      return api.useQuery(orgsQuery, options);
    },

    useOrgsQueryJurisdiction(options: QueryHookOptions<orgsQuery, orgsQueryVariables> | undefined) {
      if (options?.variables) {
        options.variables.type = OrgType.JURISDICTION;
      } else {
        ClientLogger.error('useOrgService.useOrgsQuery', 'options.variables is undefined');
      }
      return api.useQuery(orgsQuery, options);
    },

    async orgs() {
      DEBUG && ClientLogger.debug('useOrgService.orgs', `started`);
      const resp = await api.query<orgsQuery, orgsQueryVariables>({
        query: orgsQuery,
        variables: {},
        fetchPolicy: 'network-only',
      });
      DEBUG && ClientLogger.debug('useCsaApi.getMe', 'Response', resp);
      return resp;
    },

    async org(id: string): Promise<ApolloQueryResult<orgQuery>> {
      return api.query<orgQuery, orgQueryVariables>({
        query: gql`
          ${ORG_FIELDS}
          query org($id: String!) {
            org(id: $id) {
              ...Org
            }
          }
        `,
        variables: { id },
        fetchPolicy: 'network-only',
      });
    },

    async orgCreate(org: OrgCreateInput): Promise<FetchResult<orgCreateMutation>> {
      const retVal = await api.mutate<orgCreateMutation, orgCreateMutationVariables>({
        mutation: gql`
          mutation orgCreate($data: OrgCreateInput!) {
            orgCreate(data: $data) {
              id
            }
          }
        `,
        variables: { data: org },
        refetchQueries: ['orgs'],
      });
      await api.refreshMe();
      return retVal;
    },

    async orgUpdate(orgId: string, org: OrgUpdateInput): Promise<FetchResult<orgUpdateMutation>> {
      return api.mutate<orgUpdateMutation, orgUpdateMutationVariables>({
        mutation: gql`
          mutation orgUpdate($id: String!, $data: OrgUpdateInput!) {
            orgUpdate(id: $id, data: $data) {
              id
            }
          }
        `,
        variables: { data: org, id: orgId },
        refetchQueries: ['orgs'],
      });
    },

    async generateHMACSecret(orgId: string, authAlgorithm: string): Promise<FetchResult<generateHMACSecretMutation>> {
      return api.mutate<generateHMACSecretMutation, generateHMACSecretMutationVariables>({
        mutation: gql`
          mutation generateHMACSecret($id: String!, $authAlgorithm: String!) {
            generateHMACSecret(id: $id, authAlgorithm: $authAlgorithm)
          }
        `,
        variables: { authAlgorithm, id: orgId },
        refetchQueries: ['orgs'],
      });
    },

    async orgOrgCreate(orgRelation: OrgRelation, org1Id: string, org2Id: string): Promise<FetchResult<orgOrgCreateMutation>> {
      const orgRelationInput: OrgRelationInput = {
        type: orgRelation,
        org1Id,
        org2Id,
      };
      return api.mutate({
        mutation: gql`
          mutation orgOrgCreate($data: OrgRelationInput!) {
            orgOrgCreate(data: $data) {
              id
              relation
              status
            }
          }
        `,
        variables: { data: orgRelationInput },
        refetchQueries: ['orgs'],
      });
    },

    async orgOrgDelete(orgRelation: OrgRelation, org1Id: string, org2Id: string): Promise<FetchResult<orgOrgCreateMutation>> {
      const orgRelationInput: OrgRelationInput = {
        type: orgRelation,
        org1Id,
        org2Id,
      };
      return api.mutate<orgOrgCreateMutation>({
        mutation: gql`
          mutation orgOrgDelete($data: OrgRelationInput!) {
            orgOrgDelete(data: $data) {
              id
              relation
              status
              org1Id
              org2Id
            }
          }
        `,
        variables: { data: orgRelationInput },
        refetchQueries: ['orgs'],
      });
    },

    async orgDelete(orgId: string): Promise<FetchResult<orgDeleteMutation>> {
      const deletedOrg = await api.mutate<orgDeleteMutation>({
        mutation: gql`
          mutation orgDelete($id: String!) {
            orgDelete(id: $id) {
              id
            }
          }
        `,
        variables: { id: orgId },
        refetchQueries: ['orgs'],
      });
      await api.refreshMe();
      return deletedOrg;
    },
    async getOrgs(params: getAllOrgsQueryVariables): Promise<ApolloQueryResult<getAllOrgsQuery>> {
      return api.query({
        query: gql`
          ${ORG_FIELDS}
          query getAllOrgs($type: OrgType!) {
            getAllOrgs(data: $type) {
              ...Org
            }
          }
        `,
        variables: {
          type: params.type,
        },
      });
    },
  });
  return apiFunctions.current;
}
